import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Subject, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap } from 'rxjs/operators';

import { StorageService } from 'ngx-webstorage-service';

import { environment } from '@environment';
import { BroadcastChannelActions } from '@common/model/broadcast-channel-actions';
import { AppStorageService } from '@common/services/app-storage.service';
import { BroadcastChannelService } from '@common/services/broadcast-channel.service';
import { TokenRefreshService } from '@common/services/token-refresh.service';

@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject<StorageService>(AppStorageService);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly apiEndPoint = environment.apiUrl + 'api';

  private readonly online = new BehaviorSubject(navigator.onLine);
  readonly isOnline$ = this.online.pipe(distinctUntilChanged());

  // The 'online' event only means the OS has a network interface up, not that our API is
  // reachable, so it triggers an immediate probe rather than being trusted on its own.
  private readonly recheck = new Subject<void>();

  // Only probed while offline, to detect recovery without waiting for real traffic to happen.
  // While online, TokenInterceptor.reportReachable() from real requests is the only signal.
  private readonly offlineRetryIntervalMs = 15 * 1000;

  private readonly pollLockKey = 'fbo_connectivity_poll_lock';
  // Safety timeout in case the owning tab crashes/navigates away mid-probe without releasing
  // the lock — not tied to any polling cadence, just generous over a normal request round-trip.
  private readonly pollLockTimeoutMs = 8 * 1000;

  private tryAcquirePollLock(): string | null {
    const existing = this.storageService.get(this.pollLockKey) as string | null;
    if (existing != null) {
      const ts = parseInt(existing.split(':')[0], 10);
      if (!Number.isNaN(ts) && Date.now() - ts < this.pollLockTimeoutMs) {
        return null; // another tab holds the lock
      }
    }
    const owner = Math.random().toString(36).slice(2);
    const value = `${Date.now()}:${owner}`;
    this.storageService.set(this.pollLockKey, value);
    return (this.storageService.get(this.pollLockKey) as string | null) === value ? owner : null;
  }

  private releasePollLock(owner: string): void {
    if ((this.storageService.get(this.pollLockKey) as string | null)?.endsWith(`:${owner}`)) {
      this.storageService.remove(this.pollLockKey);
    }
  }

  private started = false;

  // Called by TokenInterceptor on every real request's outcome, so active usage updates
  // connectivity for free instead of needing a dedicated probe to find out the same thing.
  reportReachable(reachable: boolean): void {
    if (this.online.value === reachable) {
      return;
    }
    this.online.next(reachable);
    this.broadcastChannelService.sendMessage({
      action: BroadcastChannelActions.connectivityStatusChanged,
      payload: { reachable }
    });
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.broadcastChannelService.getMessageStream()
      .pipe(filter(message => message.action === BroadcastChannelActions.connectivityStatusChanged))
      .subscribe(message => this.online.next(message.payload.reachable));

    fromEvent(window, 'offline').subscribe(() => this.reportReachable(false));
    fromEvent(window, 'online').subscribe(() => this.recheck.next());

    // 'online'/'offline' don't reliably fire after an OS sleep/resume if the network interface
    // itself never went down, so refocusing the tab is the reliable signal that we might have
    // missed something while away.
    fromEvent(document, 'visibilitychange').subscribe(() => {
      if (document.visibilityState === 'visible') {
        this.recheck.next();
      }
    });

    const offlineRecoveryTicks = this.isOnline$.pipe(
      switchMap(online => (online ? EMPTY : timer(0, this.offlineRetryIntervalMs)))
    );

    merge(of(undefined), this.recheck, offlineRecoveryTicks)
      .pipe(
        switchMap(() => {
          const owner = this.tryAcquirePollLock();
          if (!owner) {
            return EMPTY; // another tab is checking this tick
          }
          // Probing an authenticated endpoint while logged out returns 401; the token
          // interceptor then calls logoutAndRedirect() -> location.replace('/login'), which
          // reloads AppComponent and re-fires this probe: a reload loop on the login page.
          // Only probe once a session token exists (the old static-asset probe worked pre-login).
          if (!this.storageService.get('BearerToken')) {
            this.releasePollLock(owner);
            return EMPTY;
          }

          return this.http.get(`${this.apiEndPoint}/account/UserInfo`).pipe(
            map(() => true),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 401) {
                this.tokenRefreshService.logoutAndRedirect();
                return EMPTY;
              }
              // Align with TokenInterceptor.reportOutcome: any non-zero status means the
              // server answered, so it counts as reachable even if the request itself failed.
              return of(error.status !== 0);
            }),
            finalize(() => this.releasePollLock(owner))
          );
        })
      )
      .subscribe(reachable => this.reportReachable(reachable));
  }
}
